import { useState } from 'react';
import { ArrowRight, Check, Shield, Clock, Users } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PricingTable from '@/components/PricingTable';
import TestimonialSlider from '@/components/TestimonialSlider';
import ProcessSlider from '@/components/ProcessSlider';
import TestimonialFAQSection from '@/components/TestimonialFAQSection';
import ContactPopup from '@/components/ContactPopup';
import MobileSlider from '@/components/MobileSlider';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { openChat } from '@/utils/chatService';

const TrademarkRegistration = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const benefits = [
    {
      title: 'Nationwide Protection',
      description: 'Federal trademark registration provides exclusive rights across all 50 states.'
    },
    {
      title: 'Legal Presumption',
      description: 'Creates legal presumption of ownership and exclusive right to use the mark.'
    },
    {
      title: 'Public Notice',
      description: 'Provides public notice of your claim, deterring others from using similar marks.'
    },
    {
      title: 'Litigation Advantage',
      description: 'Strengthens your position in trademark infringement lawsuits.'
    },
    {
      title: 'Brand Value',
      description: 'Enhances the value of your brand and business assets.'
    },
    {
      title: 'International Rights',
      description: 'Serves as basis for obtaining trademark protection in foreign countries.'
    }
  ];

  const faqs = [
    {
      question: 'How long does trademark registration take?',
      answer: 'The trademark registration process typically takes 8-12 months from filing to approval, though timelines can vary based on USPTO workload and any office actions that may arise.'
    },
    {
      question: 'What can be trademarked?',
      answer: 'You can trademark words, phrases, logos, symbols, designs, sounds, colors, or any combination that identifies and distinguishes your goods or services from others.'
    },
    {
      question: 'How long does trademark protection last?',
      answer: 'A registered trademark can last indefinitely with proper maintenance. You must file maintenance documents between the 5th-6th year, and renewal applications every 10 years.'
    },
    {
      question: 'Do I need an attorney to register a trademark?',
      answer: 'While not legally required, having professional assistance significantly increases approval chances and helps avoid costly mistakes. Our experts guide you through the entire process.'
    },
    {
      question: 'What is the difference between TM and ®?',
      answer: 'TM indicates an unregistered trademark, while ® can only be used after your trademark is officially registered with the USPTO.'
    },
    {
      question: 'What classes should I file under?',
      answer: 'Trademark classes categorize goods and services. Our attorneys help you select the appropriate classes based on your business offerings to ensure comprehensive protection.'
    },
    {
      question: 'Can someone else use my business name?',
      answer: 'Without trademark registration, others may use similar names in different geographic areas or industries. Federal registration provides nationwide exclusive rights.'
    },
    {
      question: 'What happens if I receive an office action?',
      answer: 'Office actions are common and we respond to them on your behalf. Our experienced attorneys craft strategic responses to overcome USPTO objections.'
    },
    {
      question: 'Is my state registration enough?',
      answer: 'State registration only protects your mark within that state. Federal registration provides nationwide protection and additional legal benefits.'
    },
    {
      question: 'Can I register a trademark before using it?',
      answer: 'Yes, you can file based on "intent to use" and have up to 3 years to begin actual use before completing registration with a Statement of Use.'
    }
  ];

  return (
    <>
      <Header />
      
      {/* Hero Section - Enhanced Professional Design */}
      <section className="relative py-12 md:py-16 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="trademark-hero-section">
        {/* Enhanced Background Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(239,68,68,0.1),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(249,115,22,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
        
        {/* Dynamic Decorative Elements */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-red-600/20 to-orange-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-orange-600/15 to-red-500/15 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Centered Content */}
            <div className="space-y-6 text-white">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Trademark Registration Services
              </h1>
              <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto">
                Protect your brand identity with comprehensive trademark registration. Our experts handle 
                everything from comprehensive searches to successful registration with the USPTO.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4 justify-center">
                <Button
                  onClick={() => setIsPopupOpen(true)}
                  className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-8 py-3 rounded-full text-lg font-semibold hover:shadow-2xl"
                  data-testid="trademark-hero-get-started-btn"
                >
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                  onClick={() => openChat()}
                  variant="outline"
                  className="border-2 border-white text-white px-8 py-3 rounded-full text-lg font-semibold hover:bg-white/10"
                  data-testid="trademark-hero-chat-now-btn"
                >
                  Chat Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section - Image on Left, Content on Right */}
      <section className="py-20 bg-white" data-testid="trademark-description-section">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Side - Image */}
            <div className="order-2 md:order-1">
              <img
                src="https://images.pexels.com/photos/5668806/pexels-photo-5668806.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Trademark Registration"
                className="rounded-3xl shadow-2xl w-full h-auto"
              />
            </div>
            {/* Right Side - Content */}
            <div className="order-1 md:order-2">
              <h2 className="text-4xl font-bold mb-8">Why Register Your Trademark?</h2>
              <div className="text-lg text-gray-700 leading-relaxed space-y-4">
                <p>
                  A trademark is a valuable business asset that identifies and distinguishes your products or 
                  services from competitors. Federal trademark registration with the United States Patent and 
                  Trademark Office (USPTO) provides the strongest legal protection for your brand.
                </p>
                <p>
                  Without trademark protection, others may use similar names, logos, or slogans that could confuse 
                  customers and dilute your brand identity. Registration establishes your exclusive right to use 
                  the mark nationwide in connection with your goods or services.
                </p>
                <p>
                  Our comprehensive trademark registration service includes thorough searches, expert application 
                  preparation, filing with the USPTO, and ongoing communication throughout the examination process. 
                  We maximize your chances of approval while minimizing delays and potential rejections.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50" data-testid="trademark-benefits-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Benefits of Trademark Registration</h2>
            <p className="text-xl text-gray-600">Protect and enhance your brand value</p>
          </div>

          <MobileSlider>
            {benefits.map((benefit, idx) => (
              <div
                key={idx}
                className="bg-white p-8 rounded-2xl card-hover h-full"
                data-testid={`trademark-benefit-${idx}`}
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Check className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </MobileSlider>
        </div>
      </section>

      {/* Process Slider */}
      <ProcessSlider />

      {/* Pricing Table */}
      <PricingTable />

      {/* Testimonials & FAQs Combined */}
      <TestimonialFAQSection faqs={faqs} />

      {/* CTA Section - Strong Call to Action with Background */}
      <section className="relative py-24 overflow-hidden" data-testid="trademark-cta-section">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1758599543278-32d9d073941e?w=1920&q=80" 
            alt="Professional business team"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/90 to-slate-900/95"></div>
          <div className="absolute inset-0 bg-gradient-to-br from-red-600/20 to-orange-600/20"></div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="inline-block mb-6">
              <span className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-6 py-2 rounded-full text-sm font-semibold uppercase tracking-wide">
                Limited Time Offer
              </span>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Don't Wait—Protect Your Brand Before Someone Else Takes It
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-4 leading-relaxed">
              Every day without trademark protection puts your business at risk.
            </p>
            
            <p className="text-lg text-gray-300 mb-10 max-w-3xl mx-auto">
              Join 5,000+ businesses that trust Easy Trademark Filing for fast, reliable brand protection. 
              Our expert attorneys make the process simple with transparent pricing and 98% approval rates.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                onClick={() => setIsPopupOpen(true)}
                className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-10 py-6 rounded-full text-lg font-bold hover:shadow-2xl hover:scale-105 transition-all"
                data-testid="trademark-cta-register-btn"
              >
                Start Your Trademark Today
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                onClick={() => setIsPopupOpen(true)}
                variant="outline"
                className="border-2 border-white text-white px-10 py-6 rounded-full text-lg font-bold hover:bg-white hover:text-slate-900 transition-all"
                data-testid="trademark-cta-consultation-btn"
              >
                Get Free Consultation
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-8 text-white/90">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-400" />
                <span className="text-sm">98% Success Rate</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-400" />
                <span className="text-sm">Fast Processing</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-red-400" />
                <span className="text-sm">5,000+ Happy Clients</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </>
  );
};

export default TrademarkRegistration;